import { read, TABLE } from '../data/db';
import { annotations } from '../data/repos';
import type { AnnotationRow } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { newId } from './ids';

/**
 * B spec §4.5 — **the annotations facade** (build unit B-2, scenario 17): what the hypothesis
 * detail sheet, the workflow's Step 1 and the match record read and write.
 *
 * The A convention, unchanged: `load*` is one readonly transaction; each write dispatches
 * exactly one command and returns `DispatchResult`. No `onCommitted` — an annotation save is a
 * sheet's Save button, not a capture ack, and the sheet re-reads on success (the imperative
 * re-read convention).
 */

/** What the detail sheet's Annotate field edits. `id` absent = a new annotation. */
export interface AnnotationInput {
  readonly id?: string;
  readonly matchId: string;
  readonly hypothesisId: string;
  readonly text: string;
}

/**
 * A match's annotations, oldest first — or one hypothesis's, when the detail sheet asks.
 * `[]` for a match with none; a vanished match simply has none, and the caller's own match
 * read is what notices the difference.
 */
export function loadAnnotations(
  matchId: string,
  hypothesisId?: string,
): Promise<AnnotationRow[]> {
  return read(
    [TABLE.annotations],
    () => annotations.listForMatch(matchId, hypothesisId),
    'loadAnnotations',
  );
}

/**
 * B §4.2 — save one annotation ("applies to #7, the sub doesn't do this"). One command for
 * create and edit; the handler decides against its in-transaction read, and on the edit arm
 * only `text` can land (see the `updateAnnotation` op), so the `at` stamped here only ever
 * dates a genuinely new row.
 */
export function writeAnnotation(
  input: AnnotationInput,
  at: number = Date.now(),
): Promise<DispatchResult> {
  const row: AnnotationRow = {
    id: input.id ?? newId(),
    matchId: input.matchId,
    hypothesisId: input.hypothesisId,
    text: input.text,
    createdAt: at,
    updatedAt: at,
  };
  return dispatch({ type: 'WriteAnnotation', matchId: input.matchId, row });
}

/** B §4.2 — remove one annotation. Same window as the write; a stale target returns a value. */
export function deleteAnnotation(matchId: string, annotationId: string): Promise<DispatchResult> {
  return dispatch({ type: 'DeleteAnnotation', matchId, annotationId });
}
